import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'

import { getAmountStep, getMinAmount, isAgeRestrictedProduct, normalizeAmount } from '@entities/Product/helpers'

import { useAuth } from '@features/auth'
import { useCartStore } from '@features/cart/model/useCartStore'
import { ProductCounter } from '@features/cart/ui/product-counter'
import useAddress from '@features/delivery/hooks/useAddress'
import { useErrorInfoModal } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'


import { DEBOUNCE_TIMEOUT_UPDATE_AMOUNT } from '@shared/contants/product'
import { Product, useUpdateCartProductAmountMutation } from '@shared/generated'
import { useAgeVerification } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import Button, { type ButtonSizeVariant } from '@shared/ui/button'

import AddToCart from './add-to-cart'


interface ControlCartProps {
  classNameAddButton?: string;
  classNameControl?: string;
  isTablet: boolean;
  size?: ButtonSizeVariant;
  /**
   * Component will behave like a single block and take all required place
   * In practise this means we will disable absolute positioning for nested elements
   */
  asBlock?: boolean
  product: Product;
  isInTabBar?: boolean;
  noTotals?: boolean;
}

export default function ControlCart (props: ControlCartProps) {
  const {
    classNameAddButton,
    isTablet,
    classNameControl,
    size = 'medium',
    product,
    isInTabBar,
    noTotals = false,
    asBlock = false
  } = props
  const { t: te } = useTranslation('errors')
  const { t: tp } = useTranslation('product')
  const { isAuthorized } = useAuth()
  const { addOrUpdateProduct, isProductInCart, removeProduct, getProduct } = useCartStore()
  const { isAdult, guardAgeRestrictedAction } = useAgeVerification()
  const { addToCart: trackAddToCart } = useAnalytics()
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ updateProductAmount ] = useUpdateCartProductAmountMutation()
  const debouncedUpdate = useDebounceCallback(
    updateProductAmount,
    DEBOUNCE_TIMEOUT_UPDATE_AMOUNT,
  )
  const { shopId } = useAddressStore()
  const cartItem = getProduct(product.id)

  const HAS_PRODUCT_IN_CART = isProductInCart(product.id)
  const IS_OUT_OF_STOCK = product.stock?.amount === 0
  const isAgeControlRequired = !isAdult && isAgeRestrictedProduct(product)

  const handleAddToCart = async () => {
    const minAmount = getMinAmount(product)
    const toAddAmount = normalizeAmount(minAmount, getAmountStep(product))

    // Track initial add_to_cart from button click
    trackAddToCart(product, toAddAmount)

    addOrUpdateProduct({
      product_id: product.id,
      amount: toAddAmount,
    })

    try {
      if (isAuthorized) {
        await debouncedUpdate({
          variables: {
            shopId: shopId,
            itemData: {
              product_id: product.id,
              amount: minAmount,
              comment: cartItem?.comment,
            },
          },
        })
      }
    }
    catch (error) {
      console.error('Failed to sync add to cart:', error)
      openErrorInfoModal({
        description: te('add_to_cart'),
      })
      removeProduct(product.id)
    }
  }

  const addressFlow = useAddress(handleAddToCart)

  const handleProtectedAddToCart = () => {
    guardAgeRestrictedAction(isAgeControlRequired, addressFlow)
  }

  if (IS_OUT_OF_STOCK && !HAS_PRODUCT_IN_CART) {
    return (
      <Button
        className={cn(
          classNameAddButton,
          isInTabBar ? 'is_out_of_stock_tabbar' : null,
        )}
        size={isTablet ? 'medium' : 'large'}
        disabled
      >
        {tp('product.out_of_stock')}
      </Button>
    )
  }

  if (!HAS_PRODUCT_IN_CART) {
    return (
      <AddToCart
        className={cn(
          classNameAddButton,
          isInTabBar ? 'add_to_cart_tabbar' : null,
        )}
        size={size}
        onClick={handleProtectedAddToCart}
      />
    )
  }

  return (
    <ProductCounter
      shopId={shopId}
      className={classNameControl}
      size={size}
      product={product}
      invertInfo={isInTabBar}
      noTotals={noTotals}
      asBlock={asBlock}
    />
  )
}
